import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ControlBar from './ControlBar.jsx';
import { createYouTubeBridge, ytClip, ytEmbedUrl, ytFitScale, ytRenderSize } from '../../utils/youtube.js';
import styles from './FilmCard.module.css';

// ─── YouTube player ───────────────────────────────────────────────────────────
// The embed is laid out at 1080p and scaled down to the card, which is what makes
// YouTube serve 1080p (see utils/youtube.js). Its own controls are off; the shared
// ControlBar drives it over postMessage. Fullscreen takes the stage, not the
// iframe, so the same scale-to-fit makes it sharp at any screen size.
// ─────────────────────────────────────────────────────────────────────────────

const GIVE_UP_AUTOPLAY_MS = 2200;   // sound refused? fall back to muted rather than a dead player
const SHOW_ANYWAY_MS      = 9000;   // never hide YouTube's own error message behind a poster

export default function YouTubePlayer({ film }) {
  const stageRef  = useRef(null);
  const frameRef  = useRef(null);
  const bridgeRef = useRef(null);
  const stateRef  = useRef(-1);          // last playerState YouTube reported
  const vertical  = film.height > film.width;
  const size      = ytRenderSize();

  const [scale, setScale]     = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted]     = useState(false);
  const [ready, setReady]     = useState(false);
  const [time, setTime]       = useState(0);
  const [length, setLength]   = useState(film.duration ?? 0);

  // Fit the 1080p render into whatever the stage is right now (card or fullscreen)
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setScale(ytFitScale(el.clientWidth, el.clientHeight, vertical));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [vertical]);

  const onInfo = useCallback((info) => {
    if (typeof info.currentTime === 'number') setTime(info.currentTime);
    if (typeof info.duration === 'number' && info.duration > 0) setLength(info.duration);
    if (typeof info.muted === 'boolean') setMuted(info.muted);
    if (typeof info.playerState === 'number') {
      stateRef.current = info.playerState;
      setPlaying(info.playerState === 1 || info.playerState === 3);
      if (info.playerState === 1) setReady(true);
    }
  }, []);

  // The click that opened the card counts as a gesture, so sound is allowed. If
  // YouTube still holds playback back, start it muted instead.
  const onReady = useCallback(() => {
    const bridge = bridgeRef.current;
    bridge?.command('playVideo');
    setTimeout(() => {
      if (stateRef.current === 1 || !bridgeRef.current) return;
      bridgeRef.current.command('mute');
      bridgeRef.current.command('playVideo');
      setMuted(true);
    }, GIVE_UP_AUTOPLAY_MS);
  }, []);

  const onFrameLoad = () => {
    bridgeRef.current?.destroy();
    bridgeRef.current = createYouTubeBridge(frameRef.current, { onInfo, onReady });
  };
  useEffect(() => () => bridgeRef.current?.destroy(), []);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), SHOW_ANYWAY_MS);
    return () => clearTimeout(t);
  }, []);

  const command = (func, args) => bridgeRef.current?.command(func, args);

  const toggle = () => command(stateRef.current === 1 ? 'pauseVideo' : 'playVideo');
  const seekTo = (seconds) => {
    const t = Math.min(Math.max(seconds, 0), length || seconds);
    command('seekTo', [t, true]);
    setTime(t);
  };
  const toggleMute = () => {
    command(muted ? 'unMute' : 'mute');
    setMuted(!muted);
  };
  const toggleFullscreen = () => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else (el.requestFullscreen ?? el.webkitRequestFullscreen)?.call(el);
  };

  return (
    <div className={styles.ytStage} ref={stageRef}>
      {film.poster && <img className={styles.media} src={film.poster} alt="" draggable="false" />}
      <iframe
        ref={frameRef}
        className={`${styles.ytFrame} ${ready ? styles.ytFrameOn : ''}`}
        src={ytEmbedUrl(film.youtubeId, { origin: window.location.origin })}
        title={`${film.title} — ${film.sub}`}
        allow="autoplay; encrypted-media; picture-in-picture"
        tabIndex={-1}
        onLoad={onFrameLoad}
        style={{
          width: size.w,
          height: size.h,
          transform: `translate(-50%, -50%) scale(${scale || 0.0001})`,
          clipPath: ytClip(vertical),
        }}
      />
      {/* YouTube's own surface can't be clicked (it would show its chrome); this does the toggling */}
      <div className={styles.ytClick} onClick={toggle} />
      {!ready && <span className={styles.buffering} aria-hidden="true" />}
      <ControlBar
        playing={playing}
        muted={muted}
        time={time}
        length={length}
        onToggle={toggle}
        onMute={toggleMute}
        onFullscreen={toggleFullscreen}
        onSeekFraction={(f) => length && seekTo(f * length)}
        onSeekBy={(d) => seekTo(time + d)}
      />
    </div>
  );
}
